<template>
  <div>
    <Head title="Nuevo Despacho" />

    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <div class="flex items-center gap-3">
        <Link href="/shipments" class="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors shadow-sm">
          <i class="fa fa-arrow-left" />
        </Link>
        <div>
          <h1 class="text-3xl font-bold text-gray-800">Nuevo Despacho</h1>
          <p class="text-gray-500 text-sm">Agrupa órdenes y asígnales un transportista</p>
        </div>
      </div>
      
      <div class="flex items-center gap-3">
        <button 
          :disabled="form.processing || !isValid"
          class="px-6 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 transition-colors font-bold shadow-sm"
          @click="submit"
        >
          <i class="fa fa-save" />
          Guardar Despacho
        </button>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Columna Izquierda: Configuración del Despacho -->
      <div class="lg:col-span-1 space-y-6">
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 class="text-lg font-bold text-gray-800 mb-4 border-b border-gray-100 pb-3">1. Datos del Transporte</h2>
          
          <div class="space-y-4">
            <!-- Transportista -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Empresa Transportista <span class="text-red-500">*</span></label>
              <select 
                v-model="form.carrier_id" 
                class="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                @change="form.transport_unit_id = ''"
              >
                <option value="">Selecciona una empresa...</option>
                <option v-for="carrier in carriers" :key="carrier.id" :value="carrier.id">
                  {{ carrier.name }}
                </option>
              </select>
              <p v-if="form.errors.carrier_id" class="text-red-500 text-xs mt-1">{{ form.errors.carrier_id }}</p>
            </div>

            <!-- Unidad de Transporte -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Unidad (Vehículo) <span class="text-red-500">*</span></label>
              <select 
                v-model="form.transport_unit_id" 
                :disabled="!form.carrier_id"
                class="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-orange-500 focus:border-orange-500 transition-colors disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">Selecciona un vehículo...</option>
                <option v-for="unit in availableUnits" :key="unit.id" :value="unit.id">
                  {{ unit.license_plate }} {{ unit.driver_name ? `(${unit.driver_name})` : '' }}
                </option>
              </select>
              <p v-if="form.errors.transport_unit_id" class="text-red-500 text-xs mt-1">{{ form.errors.transport_unit_id }}</p>
            </div>

            <!-- Fecha de Salida -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Fecha de Salida</label>
              <input 
                v-model="form.dispatch_date" 
                type="date" 
                class="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              />
              <p v-if="form.errors.dispatch_date" class="text-red-500 text-xs mt-1">{{ form.errors.dispatch_date }}</p>
            </div>

            <!-- Observaciones -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Observaciones / Notas</label>
              <textarea 
                v-model="form.observations" 
                rows="3"
                class="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-orange-500 focus:border-orange-500 transition-colors resize-none"
                placeholder="Instrucciones para el chofer..."
              />
            </div>
          </div>
        </div>

        <!-- Resumen -->
        <div class="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-sm text-white p-6">
          <h2 class="text-lg font-bold mb-4 border-b border-orange-400/30 pb-3 text-orange-50">Resumen del Despacho</h2>
          
          <div class="space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-orange-100 text-sm">Órdenes a Despachar:</span>
              <span class="font-bold text-xl">{{ form.order_ids.length }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Columna Derecha: Selección de Órdenes -->
      <div class="lg:col-span-2">
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-[calc(100vh-140px)] min-h-[500px]">
          <div class="p-6 border-b border-gray-100 bg-gray-50/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 class="text-lg font-bold text-gray-800">2. Órdenes Pendientes</h2>
              <p class="text-sm text-gray-500">Selecciona las ventas que irán en este viaje.</p>
            </div>
            
            <!-- Buscador local de órdenes -->
            <div class="relative w-full sm:w-64">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="fa fa-search text-gray-400" />
              </div>
              <input 
                v-model="searchQuery" 
                type="text" 
                class="block w-full pl-10 pr-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 text-sm transition-colors shadow-sm" 
                placeholder="Buscar cliente, orden..."
              />
            </div>
          </div>

          <!-- Lista de órdenes -->
          <div class="flex-1 overflow-y-auto p-2">
            <div v-if="filteredOrders.length === 0" class="py-12 text-center">
              <div class="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <i class="fa fa-box-open text-2xl text-gray-300" />
              </div>
              <h3 class="text-gray-900 font-medium">No hay órdenes pendientes</h3>
              <p class="text-gray-500 text-sm mt-1">Todas las órdenes ya están asignadas a un despacho o la búsqueda no coincide.</p>
            </div>

            <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
              <label 
                v-for="order in filteredOrders" 
                :key="order.id"
                class="flex items-start gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer relative hover:shadow-md"
                :class="form.order_ids.includes(order.id) ? 'border-orange-500 bg-orange-50/30' : 'border-gray-100 bg-white hover:border-gray-200'"
              >
                <div class="pt-1">
                  <input 
                    v-model="form.order_ids" 
                    type="checkbox" 
                    :value="order.id"
                    class="w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between items-start mb-1">
                    <span class="font-bold text-gray-900 truncate pr-2">
                      {{ order.number_order || `Nº ${order.id}` }}
                    </span>
                    <span class="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 whitespace-nowrap">
                      {{ order.order_state || 'Pendiente' }}
                    </span>
                  </div>
                  <p class="text-sm text-gray-600 font-medium truncate mb-1">
                    <i class="fa fa-user text-gray-400 text-xs mr-1" />
                    {{ order.client?.name || order.client?.companyName || 'Cliente General' }}
                  </p>
                  <p v-if="order.shipping_info || order.client?.address" class="text-xs text-gray-500 truncate">
                    <i class="fa fa-map-marker-alt text-gray-400 mr-1" />
                    {{ order.shipping_info || order.client?.address }}
                  </p>
                </div>
              </label>
            </div>
          </div>
          
          <!-- Sticky Footer for Order count -->
          <div class="p-4 border-t border-gray-100 bg-white flex justify-between items-center text-sm shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
            <span class="text-gray-600">Mostrando {{ filteredOrders.length }} de {{ pendingOrders.length }} órdenes pendientes</span>
            <button 
              class="text-orange-600 font-medium hover:text-orange-700 transition-colors" 
              @click="selectAllFiltered"
            >
              {{ form.order_ids.length === filteredOrders.length && filteredOrders.length > 0 ? 'Deseleccionar todas' : 'Seleccionar todas' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  name: 'ShipmentsCreate',
  
  components: {
    Head,
    Link,
  },

  layout: Layout,

  props: {
    carriers: {
      type: Array,
      required: true,
    },
    pendingOrders: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      searchQuery: '',
      form: this.$inertia.form({
        carrier_id: '',
        transport_unit_id: '',
        dispatch_date: new Date().toISOString().substr(0, 10), // Hoy por defecto
        observations: '',
        order_ids: [],
      }),
    }
  },

  computed: {
    availableUnits() {
      if (!this.form.carrier_id) return []
      const carrier = this.carriers.find(c => c.id === this.form.carrier_id)
      return carrier ? carrier.transport_units : []
    },
    
    filteredOrders() {
      if (!this.searchQuery) return this.pendingOrders
      
      const query = this.searchQuery.toLowerCase()
      return this.pendingOrders.filter(order => {
        const number = (order.number_order || String(order.id)).toLowerCase()
        const clientName = (order.client?.name || '').toLowerCase()
        const companyName = (order.client?.companyName || '').toLowerCase()
        
        return number.includes(query) || clientName.includes(query) || companyName.includes(query)
      })
    },

    isValid() {
      return this.form.carrier_id 
          && this.form.transport_unit_id 
          && this.form.order_ids.length > 0
    },
  },

  methods: {
    selectAllFiltered() {
      const filteredIds = this.filteredOrders.map(o => o.id)
      
      // Check if all filtered are currently selected
      const allSelected = filteredIds.every(id => this.form.order_ids.includes(id))
      
      if (allSelected) {
        // Deselect only the filtered ones
        this.form.order_ids = this.form.order_ids.filter(id => !filteredIds.includes(id))
      } else {
        // Select all filtered ones, keeping previously selected
        const newSelection = new Set([...this.form.order_ids, ...filteredIds])
        this.form.order_ids = Array.from(newSelection)
      }
    },

    submit() {
      this.form.post('/shipments', {
        preserveScroll: true,
        onSuccess: () => {
          // You could show a success toast here
        },
      })
    },
  },
}
</script>
